/**
 * The pure decisions of the service access client (docs/plans/service-access.md,
 * sections 3 and 7). The storage, network and navigation around them live in
 * `serviceAccessClient.ts`.
 */

/** The callback HQ sends the tab back to with `?code=…&state=…`. */
export const SERVICE_ACCESS_CALLBACK_PATH = "/service-access/callback";

/** The fragment HQ's "Open in service access" link adds. */
export const SERVICE_ACCESS_FRAGMENT = "#service";

/** Refresh the access token once it has this little time left. */
const REFRESH_MARGIN_MS = 2 * 60 * 1000;

/** How recent the last user input must be for the person to count as present. */
const RECENT_INPUT_MS = 5 * 60 * 1000;

/** What a sign-in keeps in `sessionStorage` until its callback. */
export interface PendingServiceAccessSignIn {
  state: string;
  codeVerifier: string;
  /** The same-origin path to come back to, with query and fragment. */
  returnPath: string;
}

/**
 * What a service-mode page load does:
 * - `callback`: complete the HQ sign-in;
 * - `app`: run the app with the stored tokens;
 * - `sign_in`: start the HQ sign-in;
 * - `leave`: send a visitor without a token to the app's normal origin.
 */
export type ServiceAccessEntry = "callback" | "app" | "sign_in" | "leave";

/**
 * A visitor without a token only starts the HQ sign-in when this browser has
 * used service access on this origin before, or HQ's link asked for it, so a
 * customer who opens a copied service link never sees service access.
 */
export function decideServiceAccessEntry(options: {
  isCallback: boolean;
  hasTokens: boolean;
  hasUsedServiceAccess: boolean;
  hasServiceFragment: boolean;
}): ServiceAccessEntry {
  const { isCallback, hasTokens, hasUsedServiceAccess, hasServiceFragment } =
    options;
  if (isCallback) {
    return "callback";
  }
  if (hasTokens) {
    return "app";
  }
  if (hasUsedServiceAccess || hasServiceFragment) {
    return "sign_in";
  }
  return "leave";
}

/**
 * The code and verifier to exchange, or null when the callback's `state` is
 * not the one this tab's pending sign-in made, in which case the code is
 * dropped unused.
 */
export function resolveServiceAccessCallback(options: {
  pending: PendingServiceAccessSignIn | null;
  code: string | null;
  state: string | null;
}): { code: string; codeVerifier: string; returnPath: string } | null {
  const { pending, code, state } = options;
  if (!pending || !code || !state || state !== pending.state) {
    return null;
  }
  return {
    code,
    codeVerifier: pending.codeVerifier,
    returnPath: pending.returnPath,
  };
}

/**
 * `returnPath` when it is a path on `origin`, else `/`. Rejects anything a
 * browser would read as another origin, such as `//host` and `/\host`.
 */
export function parseServiceAccessReturnPath(
  returnPath: string,
  origin: string,
): string {
  if (
    !returnPath.startsWith("/") ||
    returnPath.startsWith("//") ||
    returnPath.startsWith("/\\")
  ) {
    return "/";
  }
  if (!URL.canParse(returnPath, origin)) {
    return "/";
  }
  const url = new URL(returnPath, origin);
  if (url.origin !== origin) {
    return "/";
  }
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Whether to refresh the access token now. Only a present person extends a
 * session: the tab is visible and has had real input recently, so background
 * tabs and polling let the idle timeout run.
 */
export function checkShouldRefreshServiceAccess(options: {
  /** Wall clock, in ms since the epoch. */
  nowMs: number;
  accessExpiresAtMs: number;
  isVisible: boolean;
  msSinceInput: number;
}): boolean {
  const { nowMs, accessExpiresAtMs, isVisible, msSinceInput } = options;
  if (accessExpiresAtMs - nowMs > REFRESH_MARGIN_MS) {
    return false;
  }
  return checkIsPersonPresent({ isVisible, msSinceInput });
}

/**
 * Whether a person is at the tab: it is visible and had input recently, where
 * coming into view counts as input.
 */
function checkIsPersonPresent(options: {
  isVisible: boolean;
  msSinceInput: number;
}): boolean {
  const { isVisible, msSinceInput } = options;
  return isVisible && msSinceInput <= RECENT_INPUT_MS;
}

/** RFC 7636's S256 challenge for `codeVerifier`. */
export async function buildCodeChallenge(codeVerifier: string) {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(codeVerifier),
  );
  return encodeBase64Url(new Uint8Array(digest));
}

/** A random base64url string of `byteLength` bytes, for a verifier or state. */
export function generateRandomBase64Url(byteLength: number) {
  return encodeBase64Url(crypto.getRandomValues(new Uint8Array(byteLength)));
}

function encodeBase64Url(bytes: Uint8Array) {
  return btoa(String.fromCharCode(...bytes))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/, "");
}
